import React, { useState, useEffect } from 'react';
import PaymentService from './PaymentService';
import DirectoryService from 'components/app/directory/DirectoryService';
import Alert from 'react-bootstrap/Alert';
import filterFieldsForList from '../../common/functions/filterFieldsForList';
import schema from './athleteJsonSchema';
import useHeaderGenerator from 'components/app/common/functions/useHeaderGenerator';
import useTableGenerator from '../../common/functions/useTableGenerator';
import { useNavigate } from 'react-router-dom';
import { Card, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const WODAthleteTable = () => {
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState('');
  const [noDataToShow, setNoDataToShow] = useState(true);
  const [tableGenerated, setTableGenerated] = useState();
  const [refreshList, setRefreshList] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [athletesOnStripe, setAthletesOnStripe] = useState([]);
  const [selectedAthletes, setSelectedAthletes] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [athletesByAccount, setAthletesByAccount] = useState([]);
  const [showModalEditAthlete, setShowModalEditAthlete] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [isSavingAthlete, setIsSavingAthlete] = useState(false);
  const navigate = useNavigate();

  const handleAddAthlete = () => {
    navigate('/app/wod/members/create-form/?role=WOD');
  };

  const jsonSchema = schema;

  const handleToggleAthlete = athleteIndex => {
    setSelectedAthletes(prev => ({
      ...prev,
      [athleteIndex]: !prev[athleteIndex]
    }));
  };

  const handleSelectAll = () => {
    const newSelection = {};
    athletesOnStripe.forEach((_, index) => {
      newSelection[index] = true;
    });
    setSelectedAthletes(newSelection);
  };

  const handleUnselectAll = () => {
    const newSelection = {};
    athletesOnStripe.forEach((_, index) => {
      newSelection[index] = false;
    });
    setSelectedAthletes(newSelection);
  };

  const getSelectedCount = () => {
    return Object.values(selectedAthletes).filter(Boolean).length;
  };

  const handleImportFromStripe = () => {
    setIsLoading(true);
    PaymentService.retrieveAllCustomersFromStripe()
      .then(response => {
        console.log('Customers imported from Stripe:', response);
        const updatedAthletes = response.filter(
          customer =>
            !athletesByAccount.some(
              a => a.email === customer.email
            )
        );
        setAthletesOnStripe(updatedAthletes);
        setSelectedAthletes({});
        setModalShow(true);
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const handleSaveSelectedAthletes = async () => {
    const athletesToImport = athletesOnStripe.filter(
      (_, index) => selectedAthletes[index]
    );

    if (athletesToImport.length === 0) {
      alert('Please select at least one athlete to import.');
      return;
    }

    setIsImporting(true);

    try {
      const importPromises = athletesToImport.map(athlete =>
        PaymentService.importAthleteFromStripe(athlete)
      );
      await Promise.all(importPromises);
      console.log('All athletes imported successfully');
      setAthletesOnStripe([]);
      setSelectedAthletes({});
      setModalShow(false);
      setRefreshList(true);
    } catch (error) {
      console.error('Error importing athletes:', error);
      alert('Error importing athletes. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleEditAthlete = identifier => {
    //const athlete = athletesByAccount.find(a => a.identifier === identifier);
    //console.log('Athlete', athlete, identifier);
    //if (athlete) {
      navigate(`/app/wod/members/${identifier}`);
      //setEditingAthlete(athlete);
      //setShowModalEditAthlete(true);
    //}
  };

  const handleSaveEditAthlete = async () => {
    if (!editingAthlete) {
      return;
    }
    setIsSavingAthlete(true);
    try {
      await PaymentService.saveWODAthlete(editingAthlete);
      setShowModalEditAthlete(false);
      setEditingAthlete(null);
      setRefreshList(true);
    } catch (error) {
      console.log('Error updating athlete', error);
    } finally {
      setIsSavingAthlete(false);
    }
  };

  useEffect(() => {
    const fieldsForList = filterFieldsForList(jsonSchema);
    setColumns(fieldsForList);

    DirectoryService.retrieveUsersByAccount()
      .then(data => {
        const updatedData = data.map(athlete => ({
          ...athlete,
          wodMembershipPlan: athlete.membershipPlan?.description || ''
        }));
        setAthletesByAccount(updatedData);
        const actionButtons = [
          {
            icon: 'edit',
            onClick: identifier => {
              handleEditAthlete(identifier);
              setRefreshList(true);
            },
            className: 'p-0',
            title: 'Edit',
            variant: 'action'
          }
        ];

        setNoDataToShow(updatedData.length === 0);
        setTableGenerated(
          useTableGenerator(updatedData, jsonSchema, actionButtons)
        );
        setRefreshList(false);
      })
      .catch(error => setError(error.message));
  }, [refreshList]);

  const buttonsConf = [];
  const headerGenerated = useHeaderGenerator('WODAthlete', ' ', buttonsConf);

  return (
    <>
      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        size="lg"
        centered
      >
        {isLoading ? (
          <>
            <Modal.Header closeButton>
              <Modal.Title>Loading Athletes...</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </Modal.Body>
          </>
        ) : (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                <FontAwesomeIcon icon="stripe" className="me-2" />
                Import Athletes from Stripe
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {athletesOnStripe && athletesOnStripe.length > 0 ? (
                <>
                  <div className="mb-3">
                    <p className="text-700">
                      Review and select the athletes you want to import.
                      <Badge bg="secondary" className="ms-2">
                        {getSelectedCount()} of {athletesOnStripe.length}{' '}
                        selected
                      </Badge>
                    </p>
                    <div className="d-flex gap-2 mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleSelectAll}
                        disabled={isImporting}
                      >
                        <FontAwesomeIcon icon="check-square" className="me-1" />
                        Select All
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleUnselectAll}
                        disabled={isImporting}
                      >
                        <FontAwesomeIcon icon="square" className="me-1" />
                        Unselect All
                      </Button>
                    </div>
                  </div>
                  {athletesOnStripe.map((customer, index) => (
                    <div
                      key={customer.stripeCustomerId || index}
                      className={`border rounded p-3 mb-2 ${
                        selectedAthletes[index] ? 'bg-light' : ''
                      }`}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`athlete-${index}`}
                        checked={selectedAthletes[index] || false}
                        onChange={() => handleToggleAthlete(index)}
                        disabled={isImporting}
                        label={
                          <div className="d-flex align-items-center justify-content-between w-100">
                            <div>
                              <strong>
                                {customer.firstName} {customer.lastName}
                              </strong>
                              <div className="text-muted small">
                                {customer.email}
                              </div>
                              <div className="text-muted small">
                                ID: {customer.stripeCustomerId}
                              </div>
                            </div>
                            {customer.productDescription && (
                              <Badge bg="success">
                                {customer.productDescription}
                              </Badge>
                            )}
                          </div>
                        }
                      />
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-center text-muted">Nothing to import</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setModalShow(false)}
                disabled={isImporting}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveSelectedAthletes}
                disabled={isImporting || getSelectedCount() === 0}
              >
                {isImporting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Importing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="download" className="me-2" />
                    Import {getSelectedCount()} Athlete
                    {getSelectedCount() !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
      <Modal
        show={showModalEditAthlete}
        onHide={() => setShowModalEditAthlete(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Athlete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingAthlete && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingAthlete.firstName || ''}
                  onChange={e =>
                    setEditingAthlete(prev => ({
                      ...prev,
                      firstName: e.target.value
                    }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingAthlete.lastName || ''}
                  onChange={e =>
                    setEditingAthlete(prev => ({
                      ...prev,
                      lastName: e.target.value
                    }))
                  }
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalEditAthlete(false)}
            disabled={isSavingAthlete}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEditAthlete}
            disabled={isSavingAthlete}
          >
            {isSavingAthlete ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {error}

      {noDataToShow && (
        <Alert key="warning" variant="warning">
          There's not any athletes to show.
        </Alert>
      )}
      <Card>
        <Card.Header>
          <h5 className="text-primary">Athletes</h5>
        </Card.Header>
        <Card.Body> {tableGenerated}</Card.Body>
        <Card.Footer>
          <Button icon="plus" onClick={handleAddAthlete} className="btn btn-sm">
            Add athlete
          </Button>
          <Button
            onClick={() => handleImportFromStripe()}
            className="btn btn-sm m-1"
            variant="secondary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Loading...
              </>
            ) : (
              'Import From Stripe'
            )}
          </Button>
        </Card.Footer>
      </Card>
    </>
  );
};

export default WODAthleteTable;
